import { useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { states } from "js/placeholderData/states";
import { useSelector } from "react-redux";
import FormInputError from "components/General/FormInputError";
import MaskedPhoneNumberInput from "components/General/Form/MaskedPhoneNumberInput";

export default function FACStepTwo({ formStep, setFormStep, setFACFormData }) {
  const uuid = uuidv4();
  const { t } = useTranslation();
  const { agent } = useSelector((s) => s.getAgent);

  const {
    register: FACStepTwoRegister,
    handleSubmit: FACStepTwoHandleSubmit,
    setValue,
    getValues,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      firstNameClaimer: "",
      lastNameClaimer: "",
      middleNameClaimer: "",
      streetAddressClaimer: "",
      streetAddress2Claimer: "",
      cityClaimer: "",
      stateClaimer: "",
      zipClaimer: "",
      emailClaimer: "",
      phoneClaimer: "",
    },
  });

  useEffect(() => {
    const phoneNumber = agent?.phones?.find(
      (p) => p.type?.toUpperCase() === "BUSN"
    );
    const address = agent?.addresses?.find(
      (a) => a.type?.toUpperCase() === "BUSN"
    );
    const defaultValues = {
      firstNameClaimer: agent?.parties?.person.firstName,
      lastNameClaimer: agent?.parties?.person.lastName,
      middleNameClaimer: agent?.parties?.person.middleName,
      streetAddressClaimer: address?.addressLine1,
      streetAddress2Claimer: address?.addressLine2,
      cityClaimer: address?.city,
      stateClaimer: address?.state,
      zipClaimer: address?.zip?.substring(0, 5),
      emailClaimer: agent?.parties?.email?.address,
      phoneClaimer: phoneNumber?.phoneNumber,
    };
    reset(defaultValues);
  }, [agent, reset]);

  const onSubmit = (data) => {
    setFormStep(3);
    setFACFormData((currentFormData) => {
      return {
        ...currentFormData,
        ...data,
      };
    });
  };

  return (
    <>
      {formStep === 2 && (
        <>
          <form onSubmit={FACStepTwoHandleSubmit(onSubmit)}>
            <hr />
            <div className="grid gap-4 grid-cols-1 lg:gap-6">
              <div className="grid gap-4 grid-cols-1 md:gap-y-0 md:gap-x-4 md:grid-cols-[2fr,2fr,1fr]">
                <div>
                  <label htmlFor={`${uuid}-fn-infor`} className="block mb-1">
                    {t("AgentFACStepTwo-firstNameLabel")}
                  </label>
                  <input
                    id={`${uuid}-fn-claimer`}
                    type="text"
                    disabled
                    {...FACStepTwoRegister("firstNameClaimer")}
                  />
                  <FormInputError
                    id={`${uuid}-fn-claimer-error`}
                    errorMessage={errors.firstNameClaimer?.message}
                  />
                </div>

                <div>
                  <label htmlFor={`${uuid}-ln-claimer`} className="block mb-1">
                    {t("AgentFACStepTwo-lastNameLabel")}
                  </label>
                  <input
                    id={`${uuid}-ln-claimer`}
                    type="text"
                    disabled
                    {...FACStepTwoRegister("lastNameClaimer")}
                  />
                  <FormInputError
                    id={`${uuid}-ln-claimer-error`}
                    errorMessage={errors.lastNameClaimer?.message}
                  />
                </div>

                <div>
                  <label htmlFor={`${uuid}-mn-claimer`} className="block mb-1">
                    {t("AgentFACStepTwo-middleInitialLabel")}
                  </label>
                  <input
                    id={`${uuid}-mn-claimer`}
                    type="text"
                    disabled
                    {...FACStepTwoRegister("middleNameClaimer")}
                  />
                  <FormInputError
                    id={`${uuid}-mn-claimer-error`}
                    errorMessage={errors.middleNameClaimer?.message}
                  />
                </div>
              </div>
              <div>
                <label htmlFor={`${uuid}-sa-claimer`} className="block mb-1">
                  {t("AgentFACStepTwo-mailingAddressLabel")}
                </label>
                <input
                  id={`${uuid}-sa-claimer`}
                  type="text"
                  disabled
                  {...FACStepTwoRegister("streetAddressClaimer", {
                    // required:
                    //   "Please enter your address for us to mail the packet to",
                  })}
                />
                <FormInputError
                  id={`${uuid}-sa-claimer-error`}
                  errorMessage={errors.streetAddressClaimer?.message}
                />
              </div>

              <div>
                <label htmlFor={`${uuid}-sa2-claimer`} className="block mb-1">
                  {t("AgentFACStepTwo-mailingAddressLabel2")}
                </label>
                <input
                  id={`${uuid}-sa2-claimer`}
                  type="text"
                  disabled
                  {...FACStepTwoRegister("streetAddress2Claimer")}
                />
                <FormInputError
                  id={`${uuid}-sa2-claimer-error`}
                  errorMessage={errors.streetAddress2Claimer?.message}
                />
              </div>
              <div className="grid gap-4 grid-cols-1 md:gap-y-0 md:gap-x-4 md:grid-cols-[1.5fr,1fr,1fr]">
                <div>
                  <label
                    htmlFor={`${uuid}-city-claimer`}
                    className="block mb-1"
                  >
                    {t("AgentFACStepTwo-cityLabel")}
                  </label>
                  <input
                    id={`${uuid}-city-claimer`}
                    type="text"
                    disabled
                    {...FACStepTwoRegister("cityClaimer", {
                      // required:
                      //   "Please enter your city for us to mail the packet to",
                    })}
                  />
                  <FormInputError
                    id={`${uuid}-city-claimer-error`}
                    errorMessage={errors.cityClaimer?.message}
                  />
                </div>

                <div>
                  <label
                    htmlFor={`${uuid}-state-claimer`}
                    className="block mb-1"
                  >
                    {t("AgentFACStepTwo-stateLabel")}
                  </label>
                  <select
                    id={`${uuid}-state-claimer`}
                    disabled
                    {...FACStepTwoRegister("stateClaimer", {
                      // required:
                      //   "Please enter your state for us to mail the packet to",
                    })}
                  >
                    <option value="">{t("AgentFACStepTwo-stateLabel")}</option>
                    {states.map((state, idx) => (
                      <option
                        value={state.abbreviation}
                        key={`state - ${state.name} - ${idx}`}
                      >
                        {state.name}
                      </option>
                    ))}
                  </select>

                  <FormInputError
                    id={`${uuid}-state-claimer-error`}
                    errorMessage={errors.stateClaimer?.message}
                  />
                </div>

                <div>
                  <label htmlFor={`${uuid}-zip-claimer`} className="block mb-1">
                    {t("AgentFACStepTwo-zipLabel")}
                  </label>
                  <input
                    id={`${uuid}-zip-claimer`}
                    type="text"
                    disabled
                    {...FACStepTwoRegister("zipClaimer", {
                      // required: {
                      //   value: true,
                      //   message: "Please enter your mailing zip code",
                      // },
                      // pattern: {
                      //   value: /^\d{5}$/,
                      //   message: "Please enter a valid zipcode (5 digits)",
                      // },
                      // maxLength: {
                      //   value: 5,
                      //   message: "Please enter only 5 digits for zipcode",
                      // },
                    })}
                  />
                  <FormInputError
                    id={`${uuid}-zip-claimer-error`}
                    errorMessage={errors.zipClaimer?.message}
                  />
                </div>
              </div>
              <div className="grid gap-4 grid-cols-1 md:gap-y-0 md:gap-x-4 md:grid-cols-[1fr,1.5fr]">
                <div>
                  <label id={`${uuid}-phone-claimer`} className="block mb-1">
                    {t("AgentFACStepTwo-phoneNumberLabel")}
                  </label>
                  <MaskedPhoneNumberInput
                    name="phoneClaimer"
                    describedby={`${uuid}-phone-claimer`}
                    register={FACStepTwoRegister}
                    setValue={setValue}
                    disabled
                    defaultValue={getValues("phoneClaimer")}
                    required={false}
                  />
                  <FormInputError
                    id={`${uuid}-phone-claimer-error`}
                    errorMessage={errors.phoneClaimer?.message}
                  />
                </div>
                <div>
                  <label
                    htmlFor={`${uuid}-email-claimer`}
                    className="block mb-1"
                  >
                    {t("AgentFACStepTwo-emailAddressLabel")}
                  </label>
                  <input
                    id={`${uuid}-email-claimer`}
                    type="text"
                    disabled
                    {...FACStepTwoRegister("emailClaimer", {
                      // required:
                      //   "Please enter your email address for better communication during the process",
                      // pattern: {
                      //   value: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/,
                      //   message: "Please enter a valid email",
                      // },
                    })}
                  />

                  <FormInputError
                    id={`${uuid}-email-claimer-error`}
                    errorMessage={errors.emailClaimer?.message}
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  className="ui-btn-secondary ui-btn-min-w mt-4"
                  type="button"
                  onClick={() => setFormStep((current) => current - 1)}
                >
                  {t("AgentFACStepTwo-previousLabel")}
                </button>

                <button className="ui-btn-primary ui-btn-min-w mt-4">
                  {t("AgentFACStepTwo-continueLabel")}
                </button>
              </div>
            </div>
          </form>
        </>
      )}
    </>
  );
}
